import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { teamService } from '../services/team.service';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Editor' | 'Viewer' | 'Analyst';
  status: 'Online' | 'Offline' | 'Away';
  joinedAt: string;
  lastActive?: string;
  isOwner: boolean;
  avatar?: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: TeamMember['role'];
  invitedBy: string;
  message?: string;
  status: 'pending' | 'accepted' | 'expired';
  createdAt: string;
  expiresAt: string;
}

export interface CreateInvitationData {
  email: string;
  role: TeamMember['role'];
  message?: string;
}

interface TeamState {
  // State
  teamMembers: TeamMember[];
  invitations: Invitation[];
  rolePermissions: Record<string, string[]>;
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  filterRole: string;

  // Actions
  setSearchTerm: (term: string) => void;
  setFilterRole: (role: string) => void;

  // Async Actions
  fetchTeamMembers: () => Promise<void>;
  fetchInvitations: () => Promise<void>;
  fetchRolePermissions: () => Promise<void>;
  inviteMember: (data: CreateInvitationData) => Promise<Invitation | null>;
  updateMemberRole: (id: string, role: TeamMember['role']) => Promise<TeamMember | null>;
  deleteMember: (id: string) => Promise<boolean>;
  resendInvitation: (id: string) => Promise<boolean>;
  deleteInvitation: (id: string) => Promise<boolean>;
  // acceptInvitation: (invitationId: string) => Promise<TeamMember | null>;
}

export const useTeamStore = create<TeamState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        teamMembers: [],
        invitations: [],
        rolePermissions: {},
        isLoading: false,
        error: null,
        searchTerm: '',
        filterRole: 'all',

        // Filter actions
        setSearchTerm: (term) => set({ searchTerm: term }),
        setFilterRole: (role) => set({ filterRole: role }),

        // Fetch team members
        fetchTeamMembers: async () => {
          set({ isLoading: true, error: null });
          try {
            const members = await teamService.getMembers();
            set({
              teamMembers: members,
              isLoading: false,
              error: null
            });
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch team members'
            });
          }
        },

        // Fetch invitations
        fetchInvitations: async () => {
          set({ isLoading: true, error: null });
          try {
            const invitations = await teamService.getInvitations();
            set({
              invitations,
              isLoading: false,
              error: null
            });
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch invitations'
            });
          }
        },

        // Fetch role permissions
        fetchRolePermissions: async () => {
          try {
            const permissions = await teamService.getRolePermissions();
            set({ rolePermissions: permissions });
          } catch (error) {
            console.error('Failed to fetch role permissions:', error);
          }
        },

        // Invite member
        inviteMember: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const invitation = await teamService.inviteMember(data);
            set((state) => ({
              invitations: [...state.invitations, invitation],
              isLoading: false,
              error: null
            }));
            return invitation;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to invite member'
            });
            return null;
          }
        },

        // Update member role
        updateMemberRole: async (id, role) => {
          set({ isLoading: true, error: null });
          try {
            const updatedMember = await teamService.updateMemberRole(id, role);
            set((state) => ({
              teamMembers: state.teamMembers.map(member =>
                member.id === id ? updatedMember : member
              ),
              isLoading: false,
              error: null
            }));
            return updatedMember;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update member role'
            });
            return null;
          }
        },

        // Delete member
        deleteMember: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await teamService.deleteMember(id);
            set((state) => ({
              teamMembers: state.teamMembers.filter(member => member.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete member'
            });
            return false;
          }
        },

        // Resend invitation
        resendInvitation: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await teamService.resendInvitation(id);
            set({ isLoading: false, error: null });
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to resend invitation'
            });
            return false;
          }
        },

        // Delete invitation
        deleteInvitation: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await teamService.deleteInvitation(id);
            set((state) => ({
              invitations: state.invitations.filter(invitation => invitation.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete invitation'
            });
            return false;
          }
        },

        // Accept invitation
        // acceptInvitation: async (invitationId) => {
        //   set({ isLoading: true, error: null });
        //   try {
        //     const newMember = await teamService.acceptInvitation(invitationId);
        //
        //     // Add the newly accepted member to the teamMembers list
        //     set((state) => ({
        //       teamMembers: [...state.teamMembers, newMember],
        //       invitations: state.invitations.map(inv =>
        //         inv.id === invitationId ? { ...inv, status: 'accepted' } : inv
        //       ),
        //       isLoading: false,
        //       error: null
        //     }));
        //
        //     return newMember;
        //   } catch (error) {
        //     set({
        //       isLoading: false,
        //       error: error instanceof Error ? error.message : 'Failed to accept invitation',
        //     });
        //     return null;
        //   }
        // },
      }),
      {
        name: 'team-storage',
        partialize: (state) => ({
          teamMembers: state.teamMembers,
          invitations: state.invitations,
          searchTerm: state.searchTerm,
          filterRole: state.filterRole,
          rolePermissions: state.rolePermissions,
        }),
      }
    ),
    {
      name: 'team-store',
    }
  )
);